/**
 * Audit Log Viewer Component
 *
 * Displays admin action audit trail with filtering and pagination
 * Shows: timestamp, admin, action, target user, reason
 */

import { functions } from '@/core/firebase'
import { httpsCallable } from 'firebase/functions'

/**
 * Audit log entry structure
 */
interface AuditLogEntry {
  id: string
  action: string
  performedBy: string
  performedByEmail?: string
  targetUserId?: string
  targetUserEmail?: string
  timestamp: Date | { _seconds: number, _nanoseconds: number } | string
  reason?: string
  details?: Record<string, unknown>
  [key: string]: unknown
}

interface GetAuditLogRequest {
  limit: number
  startAfter?: string
  filterByAction?: string
  filterByPerformedBy?: string
  filterByTargetUserId?: string
}

interface GetAuditLogResponse {
  entries: AuditLogEntry[]
  nextPageToken: string | null
  totalReturned: number
  hasMore: boolean
  filters: {
    action: string | null
    performedBy: string | null
    targetUserId: string | null
  }
}

// State
let currentEntries: AuditLogEntry[] = []
let currentPageToken: string | null = null

/**
 * Load audit log entries from backend
 */
async function loadAuditLog(options: {
  pageToken?: string
  filterByAction?: string
  filterByPerformedBy?: string
  filterByTargetUserId?: string
} = {}): Promise<void> {
  console.log('[Audit Log] Loading entries with options:', options)

  const container = document.getElementById('audit-log-container')
  if (!container) {
    console.error('[Audit Log] Container not found')
    return
  }

  // Show loading state
  container.innerHTML = `
    <div class="flex justify-center items-center py-12">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      <span class="ml-3 text-gray-600">Loading audit log...</span>
    </div>
  `

  try {
    // Call getAdminAuditLog callable
    const getAuditLogCallable = httpsCallable<GetAuditLogRequest, GetAuditLogResponse>(
      functions,
      'getAdminAuditLog',
    )

    const result = await getAuditLogCallable({
      limit: 50,
      startAfter: options.pageToken || undefined,
      filterByAction: options.filterByAction || undefined,
      filterByPerformedBy: options.filterByPerformedBy || undefined,
      filterByTargetUserId: options.filterByTargetUserId || undefined,
    })

    const data = result.data
    currentEntries = data.entries
    currentPageToken = data.nextPageToken

    console.log('[Audit Log] Loaded entries:', {
      count: data.totalReturned,
      hasMore: data.hasMore,
    })

    // Render audit log table
    renderAuditLogTable(data.entries)

    // Render pagination
    renderAuditPagination(data.nextPageToken)
  } catch (error: unknown) {
    console.error('[Audit Log] Error loading entries:', error)
    container.innerHTML = `
      <div class="text-center py-12">
        <div class="text-red-600 font-medium mb-2">Error Loading Audit Log</div>
        <div class="text-gray-600 text-sm">${(error as Error).message || 'Unknown error occurred'}</div>
        <button
          onclick="window.location.reload()"
          class="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
        >
          Retry
        </button>
      </div>
    `
  }
}

/**
 * Render audit log table
 */
function renderAuditLogTable(entries: AuditLogEntry[]): void {
  const container = document.getElementById('audit-log-container')
  if (!container) return

  if (entries.length === 0) {
    container.innerHTML = `
      <div class="text-center py-12 text-gray-500">
        No audit log entries found. Try adjusting your filters.
      </div>
    `
    return
  }

  const tableHTML = `
    <div class="overflow-x-auto">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Timestamp
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Admin User
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Action
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Target User
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Reason / Notes
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Details
            </th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          ${entries.map((entry) => renderAuditLogRow(entry)).join('')}
        </tbody>
      </table>
    </div>
  `

  container.innerHTML = tableHTML

  // Add event listeners for details buttons
  entries.forEach((entry) => {
    const detailsBtn = document.getElementById(`view-details-${entry.id}`)
    detailsBtn?.addEventListener('click', () => viewAuditDetails(entry))
  })
}

/**
 * Render a single audit log row
 */
function renderAuditLogRow(entry: AuditLogEntry): string {
  const timestamp = entry.timestamp?._seconds
    ? new Date(entry.timestamp._seconds * 1000).toLocaleString()
    : entry.timestamp
      ? new Date(entry.timestamp).toLocaleString()
      : 'N/A'

  const actionBadgeColor = getActionBadgeColor(entry.action)

  return `
    <tr class="hover:bg-gray-50">
      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
        ${timestamp}
      </td>
      <td class="px-6 py-4 whitespace-nowrap">
        <div class="text-sm font-medium text-gray-900">${entry.performedByEmail || 'N/A'}</div>
        <div class="text-xs text-gray-500 font-mono">${entry.performedBy || 'N/A'}</div>
      </td>
      <td class="px-6 py-4 whitespace-nowrap">
        <span class="px-2 py-1 text-xs font-medium rounded-full ${actionBadgeColor}">
          ${formatActionName(entry.action)}
        </span>
      </td>
      <td class="px-6 py-4 whitespace-nowrap">
        ${
          entry.targetUserEmail
            ? `<div class="text-sm font-medium text-gray-900">${entry.targetUserEmail}</div>`
            : ''
        }
        ${entry.targetUserId ? `<div class="text-xs text-gray-500 font-mono">${entry.targetUserId}</div>` : '<div class="text-sm text-gray-500">N/A</div>'}
      </td>
      <td class="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
        ${entry.reason || entry.notes || 'N/A'}
      </td>
      <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
        <button
          id="view-details-${entry.id}"
          class="text-indigo-600 hover:text-indigo-900"
        >
          View
        </button>
      </td>
    </tr>
  `
}

/**
 * Get badge color based on action type
 */
function getActionBadgeColor(action: string): string {
  const colorMap: Record<string, string> = {
    ASSIGN_ADMIN_ROLE: 'bg-red-100 text-red-800',
    REVOKE_ADMIN_ROLE: 'bg-orange-100 text-orange-800',
    UPDATE_USER_SUBSCRIPTION: 'bg-blue-100 text-blue-800',
    DELETE_USER: 'bg-red-100 text-red-800',
    UPDATE_PLAN_CONFIG: 'bg-purple-100 text-purple-800',
    FORCE_UPGRADE_TIER: 'bg-green-100 text-green-800',
    MANUAL_SUBSCRIPTION_EDIT: 'bg-blue-100 text-blue-800',
    LIST_USERS: 'bg-gray-100 text-gray-800',
  }

  return colorMap[action] || 'bg-gray-100 text-gray-800'
}

/**
 * Format action name for display
 */
function formatActionName(action: string): string {
  return action
    .split('_')
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ')
}

/**
 * Render pagination controls
 */
function renderAuditPagination(nextPageToken: string | null): void {
  const container = document.getElementById('audit-pagination-container')
  if (!container) return

  if (!nextPageToken) {
    container.innerHTML = `
      <div class="text-sm text-gray-500">
        Showing ${currentEntries.length} entry/entries
      </div>
    `
    return
  }

  container.innerHTML = `
    <div class="text-sm text-gray-500">
      Showing ${currentEntries.length} entry/entries
    </div>
    <button
      id="load-more-audit-btn"
      class="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
    >
      Load More
    </button>
  `

  const loadMoreBtn = document.getElementById('load-more-audit-btn')
  loadMoreBtn?.addEventListener('click', () => {
    loadAuditLog({ pageToken: nextPageToken })
  })
}

/**
 * View audit entry details (show modal with full info)
 */
function viewAuditDetails(entry: AuditLogEntry): void {
  const modal = document.createElement('div')
  modal.className
    = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'
  modal.style.backdropFilter = 'blur(4px)'

  const timestamp = entry.timestamp?._seconds
    ? new Date(entry.timestamp._seconds * 1000).toLocaleString()
    : entry.timestamp
      ? new Date(entry.timestamp).toLocaleString()
      : 'N/A'

  modal.innerHTML = `
    <div class="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 overflow-hidden max-h-[80vh] overflow-y-auto">
      <div class="p-6">
        <!-- Header -->
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-2xl font-bold text-gray-900">Audit Entry Details</h2>
          <button
            id="close-audit-details-modal"
            class="text-gray-400 hover:text-gray-600"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <!-- Body -->
        <div class="space-y-4">
          <div>
            <h3 class="text-sm font-medium text-gray-500">Entry ID</h3>
            <p class="mt-1 text-sm text-gray-900 font-mono">${entry.id}</p>
          </div>

          <div>
            <h3 class="text-sm font-medium text-gray-500">Timestamp</h3>
            <p class="mt-1 text-sm text-gray-900">${timestamp}</p>
          </div>

          <div>
            <h3 class="text-sm font-medium text-gray-500">Action</h3>
            <p class="mt-1 text-sm text-gray-900">${formatActionName(entry.action)}</p>
          </div>

          <div>
            <h3 class="text-sm font-medium text-gray-500">Performed By</h3>
            <p class="mt-1 text-sm text-gray-900">${entry.performedByEmail || 'N/A'}</p>
            <p class="mt-1 text-xs text-gray-500 font-mono">${entry.performedBy || 'N/A'}</p>
          </div>

          ${
            entry.targetUserId
              ? `
          <div>
            <h3 class="text-sm font-medium text-gray-500">Target User</h3>
            <p class="mt-1 text-sm text-gray-900">${entry.targetUserEmail || 'N/A'}</p>
            <p class="mt-1 text-xs text-gray-500 font-mono">${entry.targetUserId}</p>
          </div>
          `
              : ''
          }

          ${
            entry.reason || entry.notes
              ? `
          <div>
            <h3 class="text-sm font-medium text-gray-500">Reason / Notes</h3>
            <p class="mt-1 text-sm text-gray-900">${entry.reason || entry.notes}</p>
          </div>
          `
              : ''
          }

          <div>
            <h3 class="text-sm font-medium text-gray-500">Full Entry Data</h3>
            <pre class="mt-1 text-xs text-gray-900 bg-gray-50 p-3 rounded overflow-x-auto">${JSON.stringify(entry, null, 2)}</pre>
          </div>
        </div>

        <!-- Actions -->
        <div class="mt-6 flex justify-end">
          <button
            id="close-audit-details-btn"
            class="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  `

  document.body.appendChild(modal)

  const closeModal = () => {
    modal.remove()
  }

  modal
    .querySelector('#close-audit-details-modal')
    ?.addEventListener('click', closeModal)
  modal
    .querySelector('#close-audit-details-btn')
    ?.addEventListener('click', closeModal)
  modal.addEventListener('click', (e) => {
    if (e.target === modal) {
      closeModal()
    }
  })
}

/**
 * Initialize audit log component
 */
export function initializeAuditLog(): void {
  console.log('[Audit Log] Initializing')

  // Load initial entries
  loadAuditLog()

  // Setup filter button
  const filterBtn = document.getElementById('filter-audit-btn')
  filterBtn?.addEventListener('click', () => {
    const actionSelect = document.getElementById(
      'filter-action',
    ) as HTMLSelectElement
    const performedByInput = document.getElementById(
      'filter-performed-by',
    ) as HTMLInputElement
    const targetUserInput = document.getElementById(
      'filter-target-user',
    ) as HTMLInputElement

    loadAuditLog({
      filterByAction: actionSelect?.value || undefined,
      filterByPerformedBy: performedByInput?.value?.trim() || undefined,
      filterByTargetUserId: targetUserInput?.value?.trim() || undefined,
    })
  })

  // Setup clear filters button
  const clearBtn = document.getElementById('clear-audit-filters-btn')
  clearBtn?.addEventListener('click', () => {
    const actionSelect = document.getElementById(
      'filter-action',
    ) as HTMLSelectElement
    const performedByInput = document.getElementById(
      'filter-performed-by',
    ) as HTMLInputElement
    const targetUserInput = document.getElementById(
      'filter-target-user',
    ) as HTMLInputElement

    if (actionSelect) actionSelect.value = ''
    if (performedByInput) performedByInput.value = ''
    if (targetUserInput) targetUserInput.value = ''

    loadAuditLog()
  })
}
